import * as React from "react";
import { connect } from "react-redux";
import { match } from "react-router";
import { mapDispatchToProps } from "../../../redux/actions";
import { updateUnitDesign } from "../../../api-utilities/performanceUpdate";
import { getSoundAttenuatorInputValues, updateInputField } from "../../../api-calls/readApiService";
import {
  getValidationError,
  getSelectBoxOptions,
  getInputCSS,
  getFilteredValidationErrors,
  getNewDropdownList,
  getNewValidationErrorsList
} from "../../../utilities/pageUtils";
import { addNotification } from "../../../api-utilities/notification-tools";
import { formatNumber } from "../../../api-utilities/formatting";
import "../../../css/Accordions.scss";

// expected props
interface IProps {
  match: match<any>;
  updateRedux: any;
  targetVertex: string;
  reduxMenus: any;
}
// local state
interface IState {
  userInputs: any;
  displayFields: any;
  dropdownOptions: any[];
  validationErrors: any[];
  lastUpdatedTextbox: string;
  soundAttenuatorComponent: any;
}
class SoundAttenuator extends React.Component<IProps, IState> {
  public state: IState = {
    userInputs: null,
    displayFields: null,
    dropdownOptions: [],
    validationErrors: [],
    lastUpdatedTextbox: "",
    soundAttenuatorComponent: null
  };

  async componentDidMount() {
    await this.getSoundAttenuatorData(this.props.targetVertex);
  }

  async componentDidUpdate(prevProps: IProps) {
    console.log(prevProps.targetVertex + ":" + this.props.targetVertex);
    if (prevProps.targetVertex != this.props.targetVertex) {
      await this.getSoundAttenuatorData(this.props.targetVertex);
    }
  }

  private async getSoundAttenuatorData(targetVertex: string) {
    const { projectId, unitId } = this.props.match.params;
    const result = await getSoundAttenuatorInputValues(projectId, unitId, targetVertex);

    if (result.hasError) {
      const notification = {
        id: "soundAttenuatorInputsError",
        notificationType: "error",
        title: "Error",
        content: <div className="nf-div">GetSoundAttenuatorInputValues {result.errorMessage}</div>
      };
      addNotification(notification);
    }

    if (result.uiDataContainer && result.uiDataContainer.soundAttenuatorInputs) {
      this.setState({
        userInputs: result.uiDataContainer.soundAttenuatorInputs,
        displayFields: result.uiDataContainer.displayFields,
        dropdownOptions: result.dropdownOptions,
        validationErrors: result.validationErrors,
        soundAttenuatorComponent: result.uiDataContainer.soundAttenuator
      });
    }
  }
  private async RunPerformance(targetVertex: string){
    const { projectId, unitId } = this.props.match.params;
   
    this.props.updateRedux("UPDATE_DISPLAY", { showLoader: true });
    //Run performance to update read-only props for sound attenuator and update optimized design numbers.
    let perResponse = await updateUnitDesign(projectId, unitId);
    const result = await getSoundAttenuatorInputValues(projectId, unitId, targetVertex);

    if (result.hasError) {
      const notification = {
        id: "soundAttenuatorInputsError",
        notificationType: "error",
        title: "Error",
        content: <div className="nf-div">GetSoundAttenuatorInputValues {result.errorMessage}</div>
      };
      addNotification(notification);
    }

    if (result.uiDataContainer && result.uiDataContainer.soundAttenuatorInputs) {
      this.setState({
        userInputs: result.uiDataContainer.soundAttenuatorInputs,
        displayFields: result.uiDataContainer.displayFields,
        dropdownOptions: result.dropdownOptions,
        validationErrors: result.validationErrors,
        soundAttenuatorComponent: result.uiDataContainer.soundAttenuator
      });
    }
    this.props.updateRedux("UPDATE_DISPLAY", { showLoader: false });
    this.props.updateRedux("UPDATE_MENUS", { optimizedDesignInputChanged: false });
  }
  //This function gets called on every keystroke in a textbox.
  public async updateTextboxValue(fieldName: string, newValue: string) {
    const newState = Object.assign({}, this.state.userInputs, { [fieldName]: newValue });
    this.setState({ userInputs: newState, lastUpdatedTextbox: fieldName });
  }

  //This gets called by textbox onBlur events
  public async saveTextboxValue(fieldName: string, newValue: string) {
    //If field value hasn't changed, don't call the server
    if (this.state.lastUpdatedTextbox !== fieldName) {
      return;
    }
    this.saveInputValue(fieldName, newValue);
  }

  //Save new value to redis and DB
  public async saveInputValue(fieldName: string, newValue: string) {
    const { projectId, unitId } = this.props.match.params;

    this.removeValidationError(fieldName);
    let result = await updateInputField("updateSoundAttenuator", projectId, unitId, fieldName, newValue, this.props.targetVertex);
    if (result.success) {
      this.props.updateRedux("UPDATE_MENUS", { optimizedDesignInputChanged: true });
      //If there are any errors in the response, add them to state
      if (result.data.validationErrors != null) {
        this.addValidationErrors(result.data.validationErrors);
      }

      //If there are any dropdownLists present in the response, then update those in state
      if (result.data.dropdownOptions != null) {
        this.updateDropdowns(result.data.dropdownOptions);
      }

      //If uiDataContainer was updated with new values, then update it in state
      if (result.data.uiDataContainer != null) {
        this.setState({
          userInputs: result.data.uiDataContainer.soundAttenuatorInputs,
          displayFields: result.data.uiDataContainer.displayFields
        });
      }
    }
  }

  //Check for errors on this textbox/selectbox, and return the appropriate CSS className
  public getCSS(fieldName: string, type: string) {
    return getInputCSS(this.state.validationErrors, fieldName, type);
  }

  //Update dropdowns with new options from the server
  public updateDropdowns(newDropdownOptions: any) {
    let newList = getNewDropdownList(this.state.dropdownOptions, newDropdownOptions);
    this.setState({ dropdownOptions: newList });
  }

  //Add any validation errors we got from the server to state
  public addValidationErrors(validationErrors: any[]) {
    let newList = getNewValidationErrorsList(this.state.validationErrors, validationErrors);
    this.setState({ validationErrors: newList });
  }

  public getValidationError(fieldName: string) {
    //Call the validation method from pageUtils
    return getValidationError(this.state.validationErrors, fieldName, "validation-err-right");
  }

  //Whenever we update a field, remove any previous error for that field
  //Also reset the lastUpdatedTextbox in state
  public removeValidationError(fieldName: string) {
    let newArray = getFilteredValidationErrors(this.state.validationErrors, fieldName);
    this.setState({ validationErrors: newArray, lastUpdatedTextbox: "" });
  }
  //------------------------------------------------------------------------------------------

  public render() {
    let title =
      this.props.targetVertex == "SupplySoundAttenuatorInputs"
        ? "Supply Sound Attenuator"
        : this.props.targetVertex == "ReturnSoundAttenuatorInputs"
        ? "Return Sound Attenuator"
        : "";

    let savedValues = this.state.userInputs;
    if (!savedValues) {
      return null;
    }

    let soundAttenuatorComponent = this.state.soundAttenuatorComponent;
    if (!soundAttenuatorComponent) {
      return null;
    }
    console.log(savedValues.soundAttenuatorLength);
    console.log(this.state.dropdownOptions);

    return (
      <fieldset className="optimize-fieldset">
        <legend className="optimize-legend">{title}</legend>

        <div className="accordion-content">
          <div className="acc-fan-spacer"></div>
          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">Length</div>
              <div className="accordion-input">
                <select
                  className={this.getCSS("soundAttenuatorLength", "accordionselect")}
                  value={savedValues.soundAttenuatorLength}
                  onChange={(event) => this.saveInputValue("soundAttenuatorLength", event.currentTarget.value)}
                >
                  {getSelectBoxOptions(this.state.dropdownOptions, "soundAttenuatorLengthOptions")}
                </select>
              </div>
              {this.getValidationError("soundAttenuatorLength")}
            </div>
            <div className="accordion-label">Face Velocity</div>
            <div className="accordion-label-numeric">{formatNumber(soundAttenuatorComponent.faceVelocityFPM, 0)}</div>
          </div>

          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">63 Hz Insertion Loss</div>
              <div className="accordion-input">
                <div className="accordion-label-numeric2">{soundAttenuatorComponent.insertionLoss63Hz}</div>
              </div>
            </div>
            <div className="accordion-label">1000 Hz Insertion Loss</div>
            <div className="accordion-label-numeric">{soundAttenuatorComponent.insertionLoss1000Hz}</div>
          </div>

          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">125 Hz Insertion Loss</div>
              <div className="accordion-input">
                <div className="accordion-label-numeric2">{soundAttenuatorComponent.insertionLoss125Hz}</div>
              </div>
            </div>
            <div className="accordion-label">2000 Hz Insertion Loss</div>
            <div className="accordion-label-numeric">{soundAttenuatorComponent.insertionLoss2000Hz}</div>
          </div>

          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">250 Hz Insertion Loss</div>
              <div className="accordion-input">
                <div className="accordion-label-numeric2">{soundAttenuatorComponent.insertionLoss250Hz}</div>
              </div>
            </div>
            <div className="accordion-label">4000 Hz Insertion Loss</div>
            <div className="accordion-label-numeric">{soundAttenuatorComponent.insertionLoss4000Hz}</div>
          </div>

          <div className="accordion-row">
            <div className="acc-fieldset">
              <div className="accordion-label">500 Hz Insertion Loss</div>
              <div className="accordion-input">
                <div className="accordion-label-numeric2">{soundAttenuatorComponent.insertionLoss500Hz}</div>
              </div>
            </div>
            <div className="accordion-label">8000 Hz Insertion Loss</div>
            <div className="accordion-label-numeric">{soundAttenuatorComponent.insertionLoss8000Hz}</div>
          </div>
          <div className="accordion-row"></div>
        </div>
        <div className={this.props.reduxMenus.optimizedDesignInputChanged ? "perf-reset-btn-small drawing-update-design-margin" : "perf-reset-btn-small-disabled drawing-update-design-margin"}>
                <div onClick={() => this.RunPerformance(this.props.targetVertex)}>Update Design</div>
              </div>
      </fieldset>
    );
  }
}

//------------------ Redux ----------------------------
function mapStateToProps(state: any) {
  return {
    reduxMenus: state.reduxMenus
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(SoundAttenuator);
